import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { Drawer, List, Tag } from 'antd'
import { useSelector } from 'react-redux'

import { GlobalContext } from '../../../Providers/GlobalProvider'
import { selectTranslate } from '../../../reducers/LocaleReducer/LocalReducer'
import { isObject } from '../../../utils'
import { getUserContactSpecialyYears, getUserSpecialyYears, linkUserToContactSpecialityYears, linkUserToSpecialityYears, unlinkUserToContactSpecialityYears, unlinkUserToSpecialityYears } from '../../../utils/api/users'
import { getFullName } from '../../../utils/entities/specialityYear'
import Loading from '../../../Components/shared/Loading'
import SpecialityYearsTree from '../../../Components/antd/Trees/SpecialityYearsTree'
import IconButton from '../../../Components/antd/Buttons/IconButton'
import { faEdit, faPeopleArrows, faSave } from '@fortawesome/pro-solid-svg-icons'
import Search from 'antd/lib/input/Search'
import { onSuccess, resolvePromises } from '../../../utils/apiHelper'

const DRAWER_TITLE = 'Specialities supervised by'

const UserSpecialities = ({ selectedUser, onClose }) => {
  const { user, specialities } = useContext(GlobalContext)

  const t = useSelector(selectTranslate)

  const buildTitle = useMemo(() => selectedUser?.email ? t(DRAWER_TITLE) + ' ' + selectedUser.email : 'OMG', [selectedUser])

  const [userSpecialityYears, setUserSpecialityYears] = useState([])
  const [contactPersonsSpecialities, setContactPersonsSpecialities] = useState({})
  const [checked, setChecked] = useState([])
  const [checkedContact, setCheckedContact] = useState([])
  const [search, setSearch] = useState('')
  const [edit, setEdit] = useState(false)
  const [contact, setContact] = useState(false)
  const [loading, setLoading] = useState(false)

  const fetchUserSpecialities = useCallback((selectedUser) => {
    return getUserSpecialyYears(user, selectedUser).then(({ data }) => {
      setUserSpecialityYears(data)
      setChecked(data.map(sy => sy.id))
    })
  }, [user.id])
  const fetchUserContactPersonsSpecialities = useCallback((selectedUser) => {
    return getUserContactSpecialyYears(user, selectedUser, { contactPersonOnly: true }).then(({ data }) => {
      setContactPersonsSpecialities(data)
      setCheckedContact(data.map(sy => sy.id))
    })
  }, [user.id])

  useEffect(() => {
    if (isObject(selectedUser)) {
      setLoading(true)

      const promises = [fetchUserSpecialities(selectedUser), fetchUserContactPersonsSpecialities(selectedUser)]

      resolvePromises(promises).finally(() => setLoading(false))
    } else {
      setChecked([])
      setUserSpecialityYears([])
      setContactPersonsSpecialities({})
    }
  }, [selectedUser?.id, fetchUserSpecialities, fetchUserContactPersonsSpecialities])

  const handleChecked = useCallback((ids, checked) => {
    if (checked) {
      setChecked(prev => {
        ids = ids.filter(id => !prev.includes(id))
        return [...prev, ...ids]
      })
    } else {
      setChecked(prev => prev.filter(id => !ids.includes(id)))
    }
  }, [])

  const handleCheckedContact = useCallback((ids, checked) => {
    if (checked) {
      setCheckedContact(prev => {
        ids = ids.filter(id => !prev.includes(id))
        return [...prev, ...ids]
      })
    } else {
      setCheckedContact(prev => prev.filter(id => !ids.includes(id)))
    }
  }, [])

  const handleEditChange = () => {
    if (edit) {
      const toBeRemoved = userSpecialityYears.filter(s => !checked.includes(s.id)).map(sy => sy.id)
      const toBeAdded = checked.filter(id => !userSpecialityYears.map(sy => sy.id).includes(id))

      const promises = []

      if (toBeAdded.length > 0) {
        promises.push(linkUserToSpecialityYears(user, selectedUser, toBeAdded))
      }

      if (toBeRemoved.length > 0) {
        promises.push(unlinkUserToSpecialityYears(user, selectedUser, toBeRemoved))
      }

      resolvePromises(promises).then(() => {
        setEdit(false)

        if (promises.length > 0) {
          fetchUserSpecialities(selectedUser).finally(() => {
            onSuccess(t('user_coordonated_specialities.update.success'))
          })
        }

        if (toBeAdded.length > 0) {
          fetchUserContactPersonsSpecialities(selectedUser)
        }
      })
    } else {
      setEdit(true)
    }
  }

  const handleContactChange = () => {
    if (contact) {
      const toBeRemoved = contactPersonsSpecialities.filter(s => !checkedContact.includes(s.id)).map(sy => sy.id)
      const toBeAdded = checkedContact.filter(id => !contactPersonsSpecialities.map(sy => sy.id).includes(id))

      const promises = []

      if (toBeAdded.length > 0) {
        promises.push(linkUserToContactSpecialityYears(user, selectedUser, toBeAdded))
      }

      if (toBeRemoved.length > 0) {
        promises.push(unlinkUserToContactSpecialityYears(user, selectedUser, toBeRemoved))
      }

      resolvePromises(promises).then(() => {
        setContact(false)

        if (promises.length > 0) {
          fetchUserContactPersonsSpecialities(selectedUser).finally(() => {
            onSuccess(t('user_contact_specialities.update.success'))
          })
        }
      })
    } else {
      setContact(true)
    }
  }

  const items = userSpecialityYears.map(sy => ({ id: sy.id, name: getFullName(sy) })).filter(sy => sy.name.toLowerCase().includes(search.toLowerCase()))
  const userSpecialities = useMemo(() => {
    const specialitiesMap = userSpecialityYears.reduce((acc, sy) => {
      if (!acc[sy.speciality.id]) {
        acc[sy.speciality.id] = {
          id: sy.speciality.id,
          name: sy.specialityName,
          abbreviation: sy.specialityAbbreviation,
          specialityYears: []
        }
      }

      acc[sy.speciality.id].specialityYears.push(sy)

      return acc
    }, {})

    return Object.values(specialitiesMap)
  }, [userSpecialityYears])
  const availableSpecialities = useMemo(() => specialities.filter(s => s.name.toLowerCase().includes(search.toLowerCase())), [specialities, search])
  const availableContactSpecialities = useMemo(() => userSpecialities.filter(s => s.name.toLowerCase().includes(search.toLowerCase())), [userSpecialities, search])
  const contactSpecialitesMap = useMemo(() => checkedContact.reduce((acc, speciality) => {
    acc[speciality] = true
    return acc
  }, {}), [checkedContact])

  const handleClose = useCallback(() => {
    setUserSpecialityYears([])
    setContactPersonsSpecialities({})
    setEdit(false)
    setContact(false)
    setChecked([])
    setCheckedContact([])
    onClose()
  }, [onClose])

  return (
    <Drawer
      title={buildTitle}
      width='640px'
      onClose={handleClose}
      visible={isObject(selectedUser)}
    >
      <div className='flex-row'>
        <Search placeholder={t('Search')} value={search} onChange={e => setSearch(e.target.value)} />
        <div style={{ width: '16px' }} />
        <IconButton
          disabled={contact}
          type='primary' icon={edit ? faSave : faEdit}
          text={edit ? t('Save') : t('Edit')}
          onClick={handleEditChange}
        />
        <div style={{ width: '16px' }} />
        <IconButton
          disabled={edit || userSpecialities.length === 0}
          type='primary' icon={contact ? faSave : faPeopleArrows}
          text={contact ? t('Save') : t('common.contact_person')}
          onClick={handleContactChange}
        />
      </div>
      <div style={{ height: '8px' }} />
      <div style={{ maxHeight: 'calc(100vh - 150px)', overflowY: 'auto' }}>
        {loading && <Loading />}
        {!loading && userSpecialities.length === 0 && !edit && !contact && <div>{t('This user does not supervise any speciality.')}</div>}
        {!loading && userSpecialities.length > 0 && !edit && !contact && (
          <List
            size='small'
            bordered
            dataSource={items}
            renderItem={sy => (
              <List.Item>
                <div className='flex-row'>{sy.name}</div>
                <div className='flex-fill' />
                {contactSpecialitesMap[sy.id] && <Tag color='green'> {t('common.contact_person')} </Tag>}
              </List.Item>
            )}
          />
        )}
        {edit && (<SpecialityYearsTree checked={checked} specialities={availableSpecialities} onChecked={handleChecked} />)}
        {contact && (<SpecialityYearsTree checked={checkedContact} specialities={availableContactSpecialities} onChecked={handleCheckedContact} />)}
      </div>
    </Drawer>
  )
}

export default (UserSpecialities)
